import type {
  ContractParty,
  ContractResponsibilities,
  ContractResponsibility,
} from "@closer/shared";
import { responsibilityTitle } from "@closer/shared";
import { Button, Modal, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { ContractClientAreaSection } from "./ContractClientAreaSection";
import { ContractDepartmentDialog } from "./ContractDepartmentDialog";
import { useContractDetail } from "./contractDetailContext";
import { ContractPartiesSection } from "./ContractPartiesSection";
import { ContractResponsibleDialog, type ResponsibleAssignment } from "./ContractResponsibleDialog";
import { Bolum, Satir } from "./contractSectionCard";
import { ContractWorkingTeamsSection } from "./ContractWorkingTeamsSection";

/**
 * TARAFLAR VE ERİŞİM bölümü — "kim dahil, ne görebiliyor".
 *
 * ### ⚠ Üç kavram YAN YANA, bilerek
 *
 * Hukuki taraf · iç sorumlu · erişim sahibi. Kullanıcının en sık karıştırdığı şey tam
 * olarak bu üçünün farkı: taraf eklemek erişim VERMEZ, sorumlu ataması erişim VERMEZ,
 * erişim vermek hukuki taraf YAPMAZ. Fark ancak üçü aynı ekranda dururken anlatılabiliyor;
 * ayrı sekmelere bölseydik kullanıcı birini yaparken diğerinin ne olduğunu göremezdi.
 * Bölüm açıklamaları (`aciklama`) bu yüzden süs değil — kuralı söyleyen tek yer.
 */
export function ContractPartiesAccessPage() {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { id, kayitYazma } = useContractDetail();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [sorumluAcik, setSorumluAcik] = useState(false);
  const [departmanAcik, setDepartmanAcik] = useState(false);

  /**
   * ⚠ Anahtar KABUKTAKİYLE AYNI: üst özet şeridi de "İç sorumlular N" için bu sorguyu
   * okuyor. Aynı anahtar → tek istek, paylaşılan önbellek.
   */
  const sorumlular = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "sorumlular"),
    queryFn: async () =>
      (await api<ContractResponsibilities>(`/api/contracts/${id}/responsibilities`, { tenantId }))
        .data,
  });

  /**
   * ÜST ÖZET ŞERİDİNİN KAYNAKLARI (CLOS-1112 · rapor 4 §18 · bulgu F-4).
   *
   * ⚠ ÜÇ SORGU DA BÖLÜMLERİN ANAHTARIYLA AYNI: `taraflar` (`ContractPartiesSection`),
   * `paylasim-baglami` ve `org-davetleri` (`ContractCollaborationSection`). Bu bölüm
   * onları zaten çiziyor; şerit önbelleği OKUYOR, ek istek atmıyor.
   *
   * ⚠ KAPILAR BÖLÜMLERİNKİNDEN GENİŞ DEĞİL: bağlam `contract.view`, davetler sunucunun
   * `canManage` cevabı. Gevşek olsaydı özet, bölümün yetkisiz saydığı kullanıcıya
   * bölümün göstermediği bir sayıyı gösterirdi — §18: "private kişi/organizasyon
   * varlığını yetkisiz kullanıcıya sızdırmaz".
   */
  const ozetTaraflar = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "taraflar"),
    queryFn: async () =>
      (
        await api<{ parties: ContractParty[]; missing: string[] }>(`/api/contracts/${id}/parties`, {
          tenantId,
        })
      ).data,
  });
  const ozetBaglam = useQuery({
    queryKey: tenantKey(tenantId, "contract", id, "paylasim-baglami"),
    queryFn: async () =>
      (
        await api<{
          externalParticipants: {
            id: string;
            maskedEmail: string;
            status: string;
            accessState: "none" | "active" | "expired" | "revoked";
          }[];
          clientAreas: { workspaceTenantId: string; clientName: string }[];
          canManage: boolean;
        }>(`/api/contracts/${id}/sharing-context`, { tenantId })
      ).data,
    enabled: can("contract.view"),
  });

  /**
   * "ERİŞİM VER VE ATA" (CLOS-1138 · rapor E.2). Sunucu, hedef üyenin kayıt erişimi yoksa
   * atamayı 409 `record_access_missing_grantable` ile durduruyor; kullanıcı başka ekrana
   * gitmeden TEK onayla minimum okuma erişimi + atamayı aynı komutta tamamlar. Aktör
   * paylaşım yetkisizse (`record_access_missing`) onay teklif edilmez — sunucunun kesin
   * reddedeceği eylem teklif edilmez; sebep gösterilir.
   */
  const [erisimOnayi, setErisimOnayi] = useState<ResponsibleAssignment | null>(null);

  const erisimEksikMi = (error: unknown): "grantable" | "blocked" | null => {
    if (!(error instanceof ApiRequestError) || error.status !== 409) return null;
    const kod = error.fields?.find((f) => f.field === "membershipId")?.code;
    if (kod === "record_access_missing_grantable") return "grantable";
    if (kod === "record_access_missing") return "blocked";
    return null;
  };

  const atamaSonrasiTazele = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "sorumlular"),
      }),
      queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "contract", id, "erisimler") }),
      queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "paylasim-baglami"),
      }),
    ]);
  };

  /**
   * İÇ SORUMLU ATAMA — tek mutasyon (CLOS-1126 · CLOS-112 `[074]`): Sözleşme Sorumlusu
   * `PUT …/owner`, alan sorumlusu ve genel katkı `POST …/contributors` (alan varsa sunucu
   * atomik değiştirir — K1). 409 `record_access_missing_grantable` → tek modal
   * "Erişim ver ve ata"; aynı gövde `grantAccess: true` ile yeniden gider (E.2).
   */
  const sorumluAta = useMutation({
    mutationFn: async (govde: ResponsibleAssignment & { grantAccess?: boolean }) => {
      const { kind, ...gonderilen } = govde;
      /** İki uç iki açık çağrı: yol literal kalsın (uç↔ekran taraması, `routeReachability`). */
      if (kind === "owner") {
        return (
          await api(`/api/contracts/${id}/responsibilities/owner`, {
            method: "PUT",
            tenantId,
            body: JSON.stringify(gonderilen),
          })
        ).data;
      }
      return (
        await api(`/api/contracts/${id}/responsibilities/contributors`, {
          method: "POST",
          tenantId,
          body: JSON.stringify(gonderilen),
        })
      ).data;
    },
    onSuccess: async () => {
      await atamaSonrasiTazele();
      setSorumluAcik(false);
      setErisimOnayi(null);
      toast.success("Sorumlu atandı");
    },
    onError: (error, govde) => {
      const durum = erisimEksikMi(error);
      if (durum === "grantable" && !govde.grantAccess) {
        setErisimOnayi(govde);
        return;
      }
      toast.error(error instanceof ApiRequestError ? error.message : "Sorumlu atanamadı");
    },
  });

  /** İlgili departman ekleme (CLOS-112 `[074]`). */
  const departmanEkle = useMutation({
    mutationFn: async (govde: { departmentId: string }) =>
      (
        await api(`/api/contracts/${id}/responsibilities/departments`, {
          method: "POST",
          tenantId,
          body: JSON.stringify(govde),
        })
      ).data,
    onSuccess: async () => {
      setDepartmanAcik(false);
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "sorumlular"),
      });
      toast.success("Departman eklendi");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Departman eklenemedi");
    },
  });

  /**
   * Sorumluluk kaldırma (CLOS-112 `[074]`).
   *
   * ⚠ Tek uç üç tür için: kaldırma davranışı türe göre değişmiyor ve satırın kimliği
   * listede zaten var — türü ayrıca taşımak çağıranı gereksiz bilgiye zorlardı.
   */
  const sorumlulukKaldir = useMutation({
    mutationFn: async (responsibilityId: string) =>
      (
        await api(`/api/contracts/${id}/responsibilities/${responsibilityId}`, {
          method: "DELETE",
          tenantId,
        })
      ).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "contract", id, "sorumlular"),
      });
      toast.success("Sorumluluk kaldırıldı");
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Sorumluluk kaldırılamadı");
    },
  });

  /** Satır başına kaldırma düğmesi; kimliksiz satır (atanmamış ana sorumlu) düğme almaz. */
  /**
   * Kişi satırı: ad + (varsa) destekleyen ekip. Ekip ERİŞİM DEĞİL, bilgi (K2); "İlgili
   * departman" satırıyla karışmasın diye kişinin altında küçük yazıyla.
   */
  const kisiDegeri = (k: ContractResponsibility | null) =>
    k ? (
      <>
        {k.displayName ?? "—"}
        {k.supportingDepartmentName ? (
          <span className="block text-xs font-normal text-fg-soft">
            Destekleyen ekip: {k.supportingDepartmentName}
          </span>
        ) : null}
      </>
    ) : (
      "—"
    );
  const kaldirDugmesi = (responsibilityId: string | undefined) =>
    can("contract.edit") && kayitYazma && responsibilityId !== undefined ? (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => sorumlulukKaldir.mutate(responsibilityId)}
      >
        Kaldır
      </Button>
    ) : null;

  /**
   * ÜST ÖZET PARÇALARI — her parça KENDİ kaynağı yüklendiyse var (CLOS-1112 · §18).
   *
   * ⚠ KANONİK TERİMLER (CLOS-1128 · Halit): "Taraflar · İç sorumlular · Haricî misafirler ·
   * Müvekkil Alanı". Eski "Ortak çalışma X org / Y haricî" ve "Bekleyen davet" parçaları
   * kaldırıldı — kaldırdığımız mega paylaşım kavramını özet şeridinde yeniden yaratmıyoruz.
   * ⚠ "İç sorumlular" KİŞİ sayıyor (ana sorumlu + katkı sağlayanlar); departman bir ekip,
   * sorumlu değil (rapor §13 "İç Sorumlular ve Ekipler" — iki ad, iki kavram).
   * ⚠ "Haricî misafirler" ERİŞİMİ AKTİF misafiri sayıyor (`accessState`), daveti kabul
   * edilmişi değil: kaldırılmış misafirin daveti `accepted` kalır (Faz 8 K5).
   * ⚠ Müvekkil Alanı "Açık" = en az bir alana açılmış; "Kapalı" hedef yokluğunu da
   * kapsıyor — özet "alan var mı" değil "sözleşme müvekkile görünür mü" sorusunu cevaplıyor.
   */
  const ozetParcalari = [
    ozetTaraflar.data
      ? { key: "taraflar", metin: `Taraflar ${ozetTaraflar.data.parties.length}` }
      : null,
    sorumlular.data
      ? {
          key: "sorumlular",
          metin: `İç sorumlular ${(sorumlular.data.owner ? 1 : 0) + sorumlular.data.contributors.length}`,
        }
      : null,
    ozetBaglam.data
      ? {
          key: "misafirler",
          metin: `Haricî misafirler ${
            ozetBaglam.data.externalParticipants.filter((k) => k.accessState === "active").length
          }`,
        }
      : null,
    ozetBaglam.data
      ? {
          key: "muvekkil-alani",
          metin: `Müvekkil Alanı ${ozetBaglam.data.clientAreas.length > 0 ? "Açık" : "Kapalı"}`,
        }
      : null,
  ].filter((parca): parca is { key: string; metin: string } => parca !== null);

  return (
    <div className="flex flex-col gap-4">
      {/*
        ÜST ÖZET ŞERİDİ — KABUKTAN BURAYA İNDİ (5 Eylül ölçümü).

        ⚠ Eskiden başlığın altında, her bölümün üstünde duruyordu ve ~46px yiyordu. Ama
        taşıdığı beş sayının beşi de BU bölümün konusu: taraf, iç sorumlu, ortak çalışma,
        bekleyen davet, müvekkil alanı. Süreç ya da Kayıt bölümüne bakan kullanıcı için
        kaydın kimliği değil, başka bir bölümün detayıydı.

        ⚠ YÜKLENMEMİŞ/YETKİSİZ PARÇA ÇİZİLMİYOR; tire ya da "0" YOK. "—" bile "burada bir
        şey var ama sana kapalı" der; §18 tam olarak bunu yasaklıyor.
      */}
      {ozetParcalari.length > 0 ? (
        <ul
          aria-label="Taraflar ve erişim özeti"
          data-testid="taraflar-erisim-ozeti"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-fg-soft [&>li+li]:before:pr-2 [&>li+li]:before:content-['·']"
        >
          {ozetParcalari.map((parca) => (
            <li key={parca.key} data-testid={`ozet-${parca.key}`}>
              {parca.metin}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/*
        ⚠ TARAF BLOĞU KENDİ BÖLÜMÜNDE (CLOS-1094): satır başına ortak çalışma durumu ve
        davet penceresi gelince blok sayfanın içinde büyüyordu.
      */}
        <ContractPartiesSection contractId={id} kayitYazma={kayitYazma} />

        <Bolum
          /*
            ⚠ "Sorumlular" hukuki taraf/temsilci ile karışıyordu (rapor 13. bölüm).

            ⚠ METİN GÜNCELLENDİ (CLOS-1212, §12.3). Eskiden "sorumlu ataması tek başına
            erişim vermez" diyordu; artık Sözleşme Sorumlusu ataması `contract_manager`
            kaynaklı erişim ÜRETİYOR. Metin canlıda yakalandı: davranış değişmiş, açıklama
            eski kararı söylemeye devam ediyordu — kullanıcıya ürünün yaptığının TERSİNİ
            anlatan bir cümle, hiç açıklama olmamasından kötüdür.

            ⚠ Ayrım korunuyor: diğer sorumluluk türleri ve departman bağı hâlâ erişim
            üretmiyor. Hepsini "erişim verir" diye toplasaydık, bu kez öbür yöne yanlış
            olurdu.
          */
          baslik="İç Sorumlular ve Ekipler"
          aciklama="Bu bölüm organizasyonunuz içinde sözleşmeyi kimlerin takip ettiğini gösterir. Sözleşme Sorumlusu atamak o kişiye bu sözleşmeye erişim verir; diğer sorumluluk türleri erişim ya da hukuki temsil yetkisi vermez."
          durum={sorumlular}
          eylem={
            can("contract.edit") && kayitYazma ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setSorumluAcik(true)}
                >
                  İç sorumlu ekle
                </Button>
                {/*
                  ⚠ "Departman ekle" KALKTI: yazma yolu kapandı (CLOS-1227). Departmanı
                  sözleşmeye bağlamanın yolu artık Çalışan Ekipler bölümü. Butonu bırakıp
                  sunucudan 409 almak, kullanıcıya çalışmayan bir kapı göstermek olurdu.
                */}
              </div>
            ) : null
          }
          bos={!sorumlular.data?.owner && sorumlular.data?.contributors.length === 0}
          bosMetni="Henüz sorumlu atanmamış."
        >
          <div className="flex flex-col">
            <Satir
              etiket={responsibilityTitle({ kind: "owner", area: null })}
              deger={kisiDegeri(sorumlular.data?.owner ?? null)}
              eylem={kaldirDugmesi(sorumlular.data?.owner?.id)}
            />
            {(sorumlular.data?.contributors ?? []).map((k) => (
              <Satir
                key={k.id}
                etiket={responsibilityTitle(k)}
                deger={kisiDegeri(k)}
                eylem={kaldirDugmesi(k.id)}
              />
            ))}
            {/*
              ⚠ LEGACY departman satırları BURADAN KALKTI (CLOS-1227 kilitli kararı). Onlar
              artık Çalışan Ekipler bölümündeki küçük "Geçmiş bilgi" alanında görünüyor.
              Burada bırakmak, emekli bir kavramı ana listede diri tutmak olurdu.
            */}
            {/* Uyarı bir HATA DEĞİL, okunan durumun parçası (`[074]`). */}
            {(sorumlular.data?.warnings ?? []).map((u) => (
              <p key={u.code} className="pt-2 text-sm text-warning">
                {u.message}
              </p>
            ))}
          </div>
        </Bolum>

        {/*
        PAYLAŞIM VE ORTAK ÇALIŞMA (CLOS-144 `[125]`) — "kim görebiliyor" sorusunun cevabı.
        Taraf ve sorumlu bloklarıyla AYNI bölümde: üçü birbirinden ancak yan yana ayırt
        ediliyor.
      */}
        {/*
          FINAL BİLGİ MİMARİSİ (CLOS-1128 · Halit final kompozisyon): mega "Paylaşım ve Ortak
          Çalışma" paneli kalktı. Taraflar sekmesi ÜÇ bölüm: Sözleşmenin Tarafları, İç
          Sorumlular ve Ekipler, Müvekkil Alanı. Diğer niyetlerin tek yüzeyi başka yerde —
          iç üye/misafir → başlıktaki "Sözleşme Erişimi"; iç kalemi karşı tarafa açma → Kayıt;
          sürümü karşı tarafa açma → Belge > Karşı Tarafa Açılan Sürümler (K1).
        */}
        <ContractWorkingTeamsSection
          contractId={id}
          tenantId={tenantId}
          kayitYazma={kayitYazma}
          legacy={sorumlular.data?.departments ?? []}
        />

        <ContractClientAreaSection contractId={id} />

        <ContractDepartmentDialog
          open={departmanAcik}
          onClose={() => setDepartmanAcik(false)}
          onConfirm={(govde) => departmanEkle.mutate(govde)}
          isPending={departmanEkle.isPending}
        />
        <ContractResponsibleDialog
          contractId={id}
          open={sorumluAcik}
          onClose={() => setSorumluAcik(false)}
          onConfirm={(govde) => sorumluAta.mutate(govde)}
          isPending={sorumluAta.isPending}
          mevcut={sorumlular.data}
        />

        {/*
          TEK MODAL (rapor E.2): "Bu kullanıcının bu sözleşmeye erişimi bulunmuyor" →
          "Erişim ver ve ata" / "İptal". Yalnız kayıt-erişimi engelini çözer; rol değişmez.
        */}
        <Modal
          open={erisimOnayi !== null}
          onClose={() => setErisimOnayi(null)}
          title="Bu kullanıcının bu sözleşmeye erişimi bulunmuyor"
          footer={
            <>
              <Button variant="secondary" onClick={() => setErisimOnayi(null)}>
                İptal
              </Button>
              <Button
                loading={sorumluAta.isPending}
                onClick={() => {
                  if (!erisimOnayi) return;
                  sorumluAta.mutate({ ...erisimOnayi, grantAccess: true });
                }}
                data-testid="erisim-ver-ve-ata"
              >
                Erişim ver ve ata
              </Button>
            </>
          }
        >
          <p className="text-sm text-fg-muted">
            Ataması için üyeye yalnız bu sözleşme üzerinde <strong>görüntüleme</strong> erişimi
            verilecek ve atama aynı adımda tamamlanacak. Organizasyon rolü veya yetkileri değişmez.
          </p>
        </Modal>
      </div>
    </div>
  );
}
